import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const typingAnimation = keyframes`
  0%, 20% { content: '.'; }
  40% { content: '..'; }
  60%, 100% { content: '...'; }
`;

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  margin: 0;
  background: ${props => props.theme.colors.white};
  position: relative;
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: #11ACD6;
  border-radius: 0 10px 0 0;
  color: white;

  .agent-info {
    display: flex;
    align-items: center;
    gap: 12px;
    

    img {
      width: 40px;
      height: 40px;
      border-radius: 100%;
      object-fit: contain;
      background-color: #FFFFFF;
      padding: 6px;
    }

    .agent-name {
      font-weight: 600;
      font-size: 16px;
    }

    .agent-role {
      font-size: 14px;
      opacity: 0.8;
    }
  }

  .close-button {
    cursor: pointer;
    position: relative;
    top: -15px;
    right: -4px;
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: ${props => props.theme.colors.white};
  isolation: isolate;
  position: relative;
  contain: content;
  z-index: 1;

  /* Add a transparent overlay to ensure proper isolation */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: ${props => props.theme.colors.white};
    pointer-events: none;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.lightGray};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.gray};
    border-radius: 3px;
  }
`;

export const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 8px;
  position: relative;
`;

export const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 4px;
  margin-left: 56px;
`;

export const MessageAvatar = styled.div`
  width: 40px;
  height: 40px;
  position: absolute;
  left: 8px;
  top: -14px;
  border-radius: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: white;

  img, svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const MessageTime = styled.div`
  font-size: 10px;
  font-family: 'Inter', sans-serif;
  color: ${props => props.theme.colors.darkGray};
`;

export const MessageBubble = styled.div`
  width: calc(100% - 60px);
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  line-height: 1.5;
  align-self: flex-start;
  background: #F2F2F2;
  color: ${props => props.theme.colors.text};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  animation: ${fadeIn} 0.3s ease-in;
  margin-left: 48px;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden; /* Prevent content from flowing outside the bubble */
  isolation: isolate; /* Create a new stacking context for better isolation */
  contain: content; /* Improve containment of styles */

  /* Style for iframes */
  & iframe {
    display: block;
    width: 100%;
    min-height: 50px;
    border: none;
    background: transparent !important;
    isolation: isolate;
    contain: content;
  }

  /* Ensure any dynamically added content stays contained */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: transparent;
    pointer-events: none;
    z-index: -1;
  }

  .message-content {
    position: relative;
    z-index: 2;
    
    /* Base styles for all elements */
    * {
      font-family: 'Inter', sans-serif;
      max-width: 100%;
      background: none !important;
      background-color: transparent !important;
    }

    /* Table styles */
    .md-table {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0;
      border: 1px solid #ddd;
    }

    .md-th {
      background-color: #f5f5f5 !important;
      color: #333;
      font-weight: bold;
      padding: 8px;
      text-align: left;
      border: 1px solid #ddd;
    }

    .md-td {
      padding: 8px;
      border: 1px solid #ddd;
      background-color: transparent !important;
    }

    /* Styled div/span for elements that originally had background/color styles */
    .md-styled-div, .md-styled-span {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 4px;
      margin: 2px 0;
      background-color: rgba(0, 0, 0, 0.05) !important;
    }

    /* Links */
    a {
      color: #0066cc;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }

    /* Code blocks */
    pre, code {
      background-color: rgba(0, 0, 0, 0.05) !important;
      padding: 8px;
      border-radius: 4px;
      font-family: monospace;
      white-space: pre-wrap;
    }

    /* Lists */
    ul, ol {
      padding-left: 20px;
      margin: 8px 0;
    }

    /* Headers */
    h1, h2, h3, h4, h5, h6 {
      margin: 16px 0 8px;
      line-height: 1.2;
    }

    /* Paragraphs */
    p {
      margin: 8px 0;
    }

    /* Images */
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
    }

    /* Blockquotes */
    blockquote {
      border-left: 4px solid #ddd;
      margin: 8px 0;
      padding-left: 16px;
      color: #666;
    }
  }

  .typing-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
    
    &::after {
      content: '.';
      animation: ${typingAnimation} 1.4s infinite;
    }
  }

  .file-message {
    margin-bottom: 8px;
    font-weight: 500;
  }

  .file-attachments {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .file-attachment {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .file-info {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .file-name {
    font-weight: 500;
    font-size: 13px;
    color: ${props => props.theme.colors.text};
    word-break: break-all;
  }

  .file-meta {
    font-size: 11px;
    color: ${props => props.theme.colors.darkGray};
    margin-top: 2px;
  }

  .attachment-reference {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 4px 8px;
    background: rgba(33, 115, 255, 0.1);
    border-radius: 4px;
    font-size: 12px;
    color: ${props => props.theme.colors.primary || '#2173FF'};
    
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  p {
    margin: 0;
    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }
`;

export const InputContainer = styled.div`
  padding: 16px;
  background: ${props => props.theme.colors.white};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const InputWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.inputBackground};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 4px;
  min-height: 48px;
  max-height: 120px;
  overflow-y: auto;

  &:focus-within {
    background: ${props => props.theme.colors.white};
    border-color: ${props => props.theme.colors.primary};
  }
`;

export const Input = styled.textarea`
  flex: 1;
  padding: 8px 8px;
  border: none;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: ${props => props.theme.colors.text};
  outline: none;
  background: transparent;
  resize: none;
  min-height: 24px;
  max-height: 100px;
  margin-right: 8px;
  line-height: 1.4;
  align-self: center;

  &::placeholder {
    color: ${props => props.theme.colors.placeholder};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const SendButton = styled.button`
  padding: 8px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ChatToggleButton = styled.button`
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #2173FF;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(33, 115, 255, 0.3);
  z-index: 999999;
  transition: all 0.3s ease;
  opacity: ${props => props.$isOpen ? '0' : '1'};
  transform: ${props => props.$isOpen ? 'scale(0)' : 'scale(1)'};
  pointer-events: ${props => props.$isOpen ? 'none' : 'auto'};

  &:hover {
    transform: ${props => !props.$isOpen && 'scale(1.05)'};
    box-shadow: 0 6px 16px rgba(33, 115, 255, 0.4);
  }

  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) invert(1);
  }
`;

export const ChatWindow = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 999998;
  isolation: isolate;
`;

export const AttachButton = styled.button`
  padding: 8px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  margin-right: 4px;
  align-self: center;

  &:hover {
    background-color: ${props => props.theme.colors.hoverBackground};
  }

  &:active {
    background-color: ${props => props.theme.colors.activeBackground};
  }
`;

